// Keyboard-aware focused-field reveal for long inline pages that scroll the
// DOCUMENT (body) rather than a bounded element container. It is the document-
// scroll sibling of `useRevealFocusedField`: same reveal semantics, same options,
// same shared pure core (`revealMath`), but it scrolls the browser's document
// scroller instead of an owned element.
//
// When the normalized viewport reports the keyboard is visible and a focused
// element inside `rootRef` falls outside the usable band, this hook performs ONE
// minimal vertical document-scroll correction so the field (optionally unioned
// with a companion action) becomes visible. It never moves focus, never remounts
// content, never touches horizontal scroll, and never overrides the user's own
// scrolling after the one-time reveal.
//
// Scoping: `rootRef` is the focus scope, NOT a scroll container. Only a focused
// element contained by that root triggers this instance, so multiple independent
// forms on one page can each own their reveal without competing — the scroller
// remains the single shared document. Roots are expected to be non-overlapping.
//
// Band ownership: the visible band is viewport-based — its lower edge is derived
// solely from `usableHeightCssPx`. It does NOT depend on the current document
// scroll position, the dashboard header height, page padding, or any other chrome
// dimension. Geometry comes only from the normalized `useViewport()` state; this
// hook reads no CSS variable, raw native event, DPR, or device signal.
//
// Trailing space: this hook never adds or removes document padding. Ensuring the
// page can scroll far enough for the last fields to clear the keyboard (e.g. a
// keyboard-inset-sized trailing spacer) is the consumer layout's responsibility.
//
// @docs:
// - docs/planning/MY_POLARIS_KEYBOARD_GEOMETRY.md

import { useEffect, useRef } from 'react';
import type { RefObject } from 'react';
import { useViewport } from '../../providers/ViewportProvider';
import { computeReveal, type VerticalInterval } from './revealMath';

export interface RevealFocusedFieldInDocumentOptions {
  /** Breathing space (CSS px) kept above the revealed element. */
  topMargin?: number;
  /** Breathing space (CSS px) kept below the revealed element (also covers a
   *  reserved/sticky bottom area). */
  bottomMargin?: number;
  /** Master switch; when false the hook is inert. */
  enabled?: boolean;
  /**
   * Optional element that must remain visible together with the focused field.
   * When supplied and contained by `rootRef`, the reveal target becomes the union
   * of the focused element and this companion (e.g. a submit button). Omit or pass
   * a null/outside ref to keep field-only behaviour. Semantics match the element
   * adapter exactly (shared core).
   */
  companionRef?: RefObject<HTMLElement | null>;
}

const DEFAULT_TOP_MARGIN = 8;
const DEFAULT_BOTTOM_MARGIN = 16;

type RevealKey = { el: Element; comp: Element | null; uh: number } | null;

/** Narrow a measured rectangle to the vertical interval the shared core consumes. */
function rectToInterval(r: DOMRect): VerticalInterval {
  return { top: r.top, bottom: r.bottom };
}

/**
 * The browser's active document scroller, standards-first (`scrollingElement`)
 * with a defensive fallback. Returns `null` when there is no document (SSR) or no
 * resolvable scroller, so callers can safely no-op.
 */
function getDocumentScroller(): HTMLElement | null {
  if (typeof document === 'undefined') return null;
  const scrolling = document.scrollingElement as HTMLElement | null;
  return scrolling ?? document.documentElement ?? null;
}

/**
 * Reveal the focused element within `rootRef` by scrolling the DOCUMENT when the
 * keyboard reduces the usable region. With `options.companionRef`, the reveal
 * target becomes the union of field + companion so both stay visible (falling back
 * to field-only when the union is taller than the band). Corrective scrolling is
 * gated on `keyboardVisible` and applied at most once per (focused element,
 * companion, usable-height) combination, so duplicate geometry and unrelated
 * re-renders never re-scroll, and manual scrolling after the reveal is preserved.
 */
export function useRevealFocusedFieldInDocument(
  rootRef: RefObject<HTMLElement | null>,
  options: RevealFocusedFieldInDocumentOptions = {},
): void {
  const {
    topMargin = DEFAULT_TOP_MARGIN,
    bottomMargin = DEFAULT_BOTTOM_MARGIN,
    enabled = true,
    companionRef,
  } = options;

  // The provider is the sole geometry authority; react to its normalized state.
  const { keyboardVisible, usableHeightCssPx } = useViewport();

  // The (element, companion, usable-height) that was last revealed. Dedupes repeat
  // work and — consulted only on a focus change or viewport transition — leaves the
  // user's own scrolling untouched.
  const lastKeyRef = useRef<RevealKey>(null);
  const frameRef = useRef<number | null>(null);

  useEffect(() => {
    const root = rootRef.current;

    const cancel = () => {
      if (frameRef.current !== null) {
        window.cancelAnimationFrame(frameRef.current);
        frameRef.current = null;
      }
    };

    const commit = () => {
      frameRef.current = null;
      const r = rootRef.current;
      const scroller = getDocumentScroller();
      const active = typeof document !== 'undefined' ? document.activeElement : null;
      // Only act for a meaningful focused element scoped inside this root, with a
      // resolvable document scroller. This containment gate is also what keeps
      // multiple independently-scoped instances from competing.
      if (!r || !scroller || !active || active === r || active === document.body || !r.contains(active)) return;

      // Viewport-based band: lower edge from usable height only; NOT the document
      // scroll position and NOT any chrome dimension. Same coordinate space as the
      // field's viewport rect, so the shared core's math applies unchanged.
      const companion = companionRef?.current ?? null;
      const includeCompanion = !!companion && companion !== active && r.contains(companion);

      const { scroll, changed } = computeReveal({
        band: { top: topMargin, bottom: usableHeightCssPx - bottomMargin },
        field: rectToInterval(active.getBoundingClientRect()),
        companion: includeCompanion ? rectToInterval(companion!.getBoundingClientRect()) : null,
        scroll: scroller.scrollTop,
        maxScroll: scroller.scrollHeight - scroller.clientHeight,
      });

      if (changed) scroller.scrollTop = scroll; // document vertical offset only; scrollLeft untouched
      lastKeyRef.current = { el: active, comp: companion, uh: usableHeightCssPx };
    };

    const maybeReveal = () => {
      if (!enabled) return;
      // Keyboard hidden: never correct, and reset the dedupe so a later open cycle
      // can reveal again. No scroll restoration is forced.
      if (!keyboardVisible) {
        lastKeyRef.current = null;
        return;
      }
      const r = rootRef.current;
      if (!r || typeof document === 'undefined') return;
      const active = document.activeElement;
      if (!active || active === r || active === document.body || !r.contains(active)) return;

      const companion = companionRef?.current ?? null;
      const last = lastKeyRef.current;
      // Dedupe on the (focused element, companion, usable-height) triple so a
      // companion change re-evaluates but duplicate geometry / unrelated re-renders
      // and later manual scrolling do not.
      if (last && last.el === active && last.comp === companion && last.uh === usableHeightCssPx) return;

      // Measure after the viewport-driven layout has settled.
      cancel();
      frameRef.current = window.requestAnimationFrame(commit);
    };

    // Trigger 1: focus change within this root (delegated, not global) — a focus
    // in another root's subtree does not bubble here, so instances stay isolated.
    root?.addEventListener('focusin', maybeReveal);
    // Trigger 2: a viewport transition (this effect re-runs when keyboardVisible or
    // usableHeightCssPx changes).
    maybeReveal();

    return () => {
      root?.removeEventListener('focusin', maybeReveal);
      cancel();
    };
  }, [rootRef, companionRef, keyboardVisible, usableHeightCssPx, enabled, topMargin, bottomMargin]);
}

export default useRevealFocusedFieldInDocument;
